import { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, IconButton,
  CircularProgress, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  Tabs, Tab, Divider, FormHelperText,
} from '@mui/material'
import {
  Add, Delete, People, ArrowBack, ContentCopy,
} from '@mui/icons-material'
import { tenantApi } from '../services/api'
import TenantProvisioningPanel from '../components/TenantProvisioningPanel'

// Tiers a platform admin may provision. Mirrors TenantTier in
// services/tenant/main.py; 'free' and 'custom' are deliberately omitted —
// neither is sold, and creating one by accident is worse than not
// offering it. Entitlement eligibility follows the tier: only Enterprise
// and Design Partner may hold selectively-provisioned features.
const TIER_OPTIONS = [
  { value: 'TRIAL', label: 'Trial', hint: 'Time-limited evaluation. Add-on features unavailable.' },
  { value: 'BUSINESS', label: 'Business', hint: 'Compliance Posture available as a paid add-on.' },
  { value: 'ENTERPRISE', label: 'Enterprise', hint: 'Eligible for all selectively-provisioned features.' },
  { value: 'DESIGN_PARTNER', label: 'Design Partner', hint: 'Unrestricted access; bypasses tier checks platform-wide.' },
]

const TenantsPage = () => {
  const [tenants, setTenants] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [formData, setFormData] = useState({
    name: '', company_name: '', tier: 'TRIAL', contact_email: '', phone: '',
    admin_email: '', admin_name: '',
  })
  const [phoneError, setPhoneError] = useState('')
  const [adminEmailError, setAdminEmailError] = useState('')

  // Detail view
  const [selectedTenant, setSelectedTenant] = useState<any>(null)
  const [detailTab, setDetailTab] = useState(0)

  // Users — read-only from here.
  //
  // Creating, deleting and password-resetting a tenant user are all
  // require_tenant_role(TENANT_ADMIN), which a platform admin is not: the two
  // schemas have no shared ordering. This page is platform-admin-only, so those
  // controls could never succeed and are gone rather than left to 403. Tenant
  // administrators manage their own users on the Team page.
  //
  // Listing still works, because list_tenant_users uses the non-strict
  // enforce_tenant_access, which platform admins pass.
  const [users, setUsers] = useState<any[]>([])
  const [usersLoading, setUsersLoading] = useState(false)

  useEffect(() => { fetchTenants() }, [])

  const fetchTenants = async () => {
    try {
      const res = await tenantApi.getTenants()
      setTenants(res.data.tenants || res.data || [])
    } catch (e) {
      console.error('Failed to fetch tenants:', e)
    } finally {
      setLoading(false)
    }
  }

  const validatePhone = (phone: string) => {
    if (!phone) return ''
    const cleaned = phone.replace(/[\s\-\(\)]/g, '')
    if (!/^\+?[0-9]{7,15}$/.test(cleaned)) return 'Invalid phone number'
    return ''
  }

  const handlePhoneChange = (value: string) => {
    setFormData({ ...formData, phone: value })
    setPhoneError(validatePhone(value))
  }

  const handleCreate = async () => {
    const pErr = validatePhone(formData.phone)
    if (pErr) { setPhoneError(pErr); return }
    // Required here even though the API accepts a tenant without one.
    //
    // A tenant created with no administrator can never be given one: every
    // route that grants tenant-admin requires being one already, and the only
    // unauthenticated path — signup → verify-email — grants `viewer`. The
    // tenant would also have no email_domain, which is derived from this
    // address, so domain-based signup could not resolve it either. Such a
    // tenant has to be recreated.
    if (!formData.admin_email.trim()) {
      setAdminEmailError(
        'Required. A tenant created without an administrator cannot be given '
        + 'one later — it would have to be recreated.')
      return
    }
    try {
      const slug = formData.company_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
      await tenantApi.createTenant({
        name: formData.company_name,
        subdomain: slug,
        primary_contact_name: formData.name,
        primary_contact_email: formData.contact_email,
        phone: formData.phone,
        tier: formData.tier.toLowerCase(),
        admin_email: formData.admin_email.trim(),
        admin_name: formData.admin_name.trim() || undefined,
      })
      setDialogOpen(false)
      setFormData({
        name: '', company_name: '', tier: 'TRIAL', contact_email: '', phone: '',
        admin_email: '', admin_name: '',
      })
      setPhoneError('')
      setAdminEmailError('')
      fetchTenants()
    } catch (e: any) {
      const detail = e.response?.data?.detail || 'Failed to create tenant'
      alert(detail)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this tenant?')) return
    try {
      await tenantApi.deleteTenant(id)
      if (selectedTenant?.id === id) setSelectedTenant(null)
      fetchTenants()
    } catch (e) {
      console.error('Failed to delete tenant:', e)
    }
  }

  const openTenantDetail = async (tenant: any) => {
    setSelectedTenant(tenant)
    setDetailTab(0)
    fetchUsers(tenant.id)
  }

  const fetchUsers = async (tenantId: string) => {
    setUsersLoading(true)
    try {
      const res = await tenantApi.getTenantUsers(tenantId)
      setUsers(res.data.users || res.data || [])
    } catch (e) {
      console.error('Failed to fetch users:', e)
      setUsers([])
    } finally {
      setUsersLoading(false)
    }
  }

  const tierColor = (tier: string) => {
    const map: any = { enterprise: 'primary', business: 'info', team: 'success', trial: 'warning', free: 'default' }
    return map[tier?.toLowerCase()] || 'default'
  }

  const statusColor = (status: string) => {
    const map: any = { active: 'success', trial: 'warning', suspended: 'error', pending: 'default' }
    return map[status?.toLowerCase()] || 'default'
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>

  // Detail view
  if (selectedTenant) {
    const t = selectedTenant
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => setSelectedTenant(null)} sx={{ mb: 2 }}>
          Back to Tenants
        </Button>
        <Typography variant="h4" sx={{ mb: 1 }}>{t.name}</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Chip label={t.tier} color={tierColor(t.tier) as any} size="small" />
          <Chip label={t.status || 'active'} color={statusColor(t.status) as any} size="small" />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 3 }}>
          <Typography variant="caption" color="text.secondary">ID: {t.id}</Typography>
          <IconButton size="small" onClick={() => navigator.clipboard.writeText(t.id)} title="Copy tenant ID">
            <ContentCopy sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} sx={{ mb: 3 }}>
          <Tab label="Details" />
          <Tab label={`Users (${users.length})`} />
          <Tab label="Features" />
        </Tabs>

        {detailTab === 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Contact Information</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Contact Name</Typography>
                  <Typography>{t.primary_contact_name || t.name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Company</Typography>
                  <Typography>{t.name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography>{t.primary_contact_email}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Phone</Typography>
                  <Typography>{t.phone || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Subdomain</Typography>
                  <Typography>{t.subdomain}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Created</Typography>
                  <Typography>{new Date(t.created_at).toLocaleDateString()}</Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ mb: 2 }}>Subscription</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Tier</Typography>
                  <Typography>{t.tier}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Trial Ends</Typography>
                  <Typography>{t.trial_ends_at ? new Date(t.trial_ends_at).toLocaleDateString() : '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Scans</Typography>
                  <Typography>{t.total_scans || 0}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Users</Typography>
                  <Typography>{t.total_users || 0}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {detailTab === 2 && (
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Features</Typography>
              <TenantProvisioningPanel
                tenantId={selectedTenant.id}
                tenantTier={selectedTenant.tier}
              />
            </CardContent>
          </Card>
        )}

        {detailTab === 1 && (
          <Card>
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6">Users</Typography>
                <Typography variant="caption" color="text.secondary">
                  Read-only. A tenant's own administrator manages its users, on their Team page.
                </Typography>
              </Box>

              {usersLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
              ) : users.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  No users. The first administrator is created with the tenant — a tenant
                  that has none cannot be given one, and has to be recreated.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Last Login</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((u: any) => (
                        <TableRow key={u.id}>
                          <TableCell>{u.name}</TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell><Chip label={u.role} size="small" /></TableCell>
                          <TableCell>
                            <Chip label={u.status} size="small"
                              color={u.status === 'active' ? 'success' : u.status === 'locked' ? 'error' : 'default'} />
                          </TableCell>
                          <TableCell>{u.last_login ? new Date(u.last_login).toLocaleString() : 'Never'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        )}

      </Box>
    )
  }

  // List view
  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Tenants</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>
          New Tenant
        </Button>
      </Box>

      <Card>
        <CardContent>
          {tenants.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">No tenants yet</Typography>
              <Button variant="contained" sx={{ mt: 2 }} onClick={() => setDialogOpen(true)}>
                Create First Tenant
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Company</TableCell>
                    <TableCell>Tenant ID</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Tier</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenants.map((t: any) => (
                    <TableRow key={t.id} hover sx={{ cursor: 'pointer' }}
                      onClick={() => openTenantDetail(t)}>
                      <TableCell>{t.name}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {t.id?.substring(0, 8)}...
                          </Typography>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(t.id) }} title="Copy full ID">
                            <ContentCopy sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>{t.primary_contact_name || t.primary_contact_email}</TableCell>
                      <TableCell><Chip label={t.tier} color={tierColor(t.tier) as any} size="small" /></TableCell>
                      <TableCell>
                        <Chip label={t.status || 'active'} color={statusColor(t.status) as any} size="small" />
                      </TableCell>
                      <TableCell>{new Date(t.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); openTenantDetail(t) }}>
                          <People />
                        </IconButton>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(t.id) }}>
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create Tenant Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Tenant</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth label="Contact Name" margin="normal" required
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
          />
          <TextField
            fullWidth label="Company Name" margin="normal" required
            value={formData.company_name}
            onChange={e => setFormData({ ...formData, company_name: e.target.value })}
          />
          <TextField
            fullWidth label="Contact Email" margin="normal" type="email" required
            value={formData.contact_email}
            onChange={e => setFormData({ ...formData, contact_email: e.target.value })}
          />
          <TextField
            fullWidth label="Phone Number" margin="normal"
            value={formData.phone}
            onChange={e => handlePhoneChange(e.target.value)}
            error={!!phoneError}
            helperText={phoneError || 'e.g. +1-555-123-4567'}
            placeholder="+1-555-123-4567"
          />
          {/* The first administrator, sent as admin_email / admin_name.
              Without it POST /tenants creates a tenant nobody can administer,
              which is not recoverable through any route. */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2">First administrator</Typography>
          <TextField
            fullWidth label="Administrator Email" margin="normal" type="email" required
            value={formData.admin_email}
            onChange={e => {
              setFormData({ ...formData, admin_email: e.target.value })
              if (adminEmailError) setAdminEmailError('')
            }}
            error={!!adminEmailError}
            helperText={adminEmailError
              || 'Becomes the tenant administrator, and sets the tenant’s email '
                 + 'domain. They choose their own password from an emailed link — '
                 + 'none is set here.'}
          />
          <TextField
            fullWidth label="Administrator Name" margin="normal"
            value={formData.admin_name}
            onChange={e => setFormData({ ...formData, admin_name: e.target.value })}
            helperText="Optional. Defaults to the part of the email before the @."
          />
          {/* The API has always accepted `tier`, but no control existed, so
              every tenant was created on Trial regardless of the contract. */}
          <FormControl fullWidth margin="normal">
            <InputLabel id="tenant-tier-label">Tier</InputLabel>
            <Select
              labelId="tenant-tier-label"
              label="Tier"
              value={formData.tier}
              onChange={e => setFormData({ ...formData, tier: e.target.value })}
            >
              {TIER_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {TIER_OPTIONS.find(o => o.value === formData.tier)?.hint}
            </FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!!phoneError}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TenantsPage
